import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  notificationTemplateService,
  NotifyTemplateType,
  CommonState,
  TEMPLATE_PARAMS,
} from '../../services/notification-template.service';
import type { CreateNotifyTemplateDto, UpdateNotifyTemplateDto } from '../../services/notification-template.service';
import { useTenants } from '../../hooks/useTenants';

const { Title } = Typography;

const TYPE_OPTIONS = [
  { value: NotifyTemplateType.NONE, label: 'None' },
  { value: NotifyTemplateType.ARTICLE, label: 'Article' },
  { value: NotifyTemplateType.PRODUCT, label: 'Product' },
  { value: NotifyTemplateType.ORDER, label: 'Order' },
  { value: NotifyTemplateType.OPEN_LINK, label: 'Open Link' },
];

const STATE_OPTIONS = [
  { value: CommonState.ENABLED, label: 'Active' },
  { value: CommonState.DISABLED, label: 'Inactive' },
];

export const NotificationTemplateForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedParam, setDraggedParam] = useState<string | null>(null);

  const { tenants, loading: tenantsLoading } = useTenants({ limit: 100 });

  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const titleCursorRef = useRef<number>(0);
  const notifyTitleCursorRef = useRef<number>(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    notificationTemplateService.getNotificationTemplateById(id)
      .then((tpl) => {
        form.setFieldsValue({
          title: tpl.title,
          notify_title: tpl.notify_title,
          content: tpl.content,
          code: tpl.code,
          type: tpl.type,
          state: tpl.state,
          tenant_id: tpl.tenant_id,
        });
      })
      .catch(() => message.error('Không tải được template'))
      .finally(() => setLoading(false));
  }, [id, form]);

  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    setDraggedParam(key);
    e.dataTransfer.setData('text/plain', key);
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleDragEnd = useCallback(() => setDraggedParam(null), []);

  const handleDropToInput = useCallback((
    e: React.DragEvent,
    fieldName: string,
    cursorRef: React.RefObject<number>,
  ) => {
    e.preventDefault();
    const key = e.dataTransfer.getData('text/plain');
    if (!key) return;
    const token = `{{${key}}}`;
    const current = (form.getFieldValue(fieldName) as string) ?? '';
    const pos = cursorRef.current;
    const next = current.slice(0, pos) + token + current.slice(pos);
    form.setFieldsValue({ [fieldName]: next });
    setDraggedParam(null);
  }, [form]);

  const handleDropToContent = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const key = e.dataTransfer.getData('text/plain');
    if (!key) return;
    const token = `{{${key}}}`;
    const el = contentRef.current;
    if (!el) {
      const current = (form.getFieldValue('content') as string) ?? '';
      form.setFieldsValue({ content: current + token });
    } else {
      const start = el.selectionStart ?? el.value.length;
      const current = el.value;
      const next = current.slice(0, start) + token + current.slice(start);
      form.setFieldsValue({ content: next });
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    }
    setDraggedParam(null);
  }, [form]);

  const handleInsertParam = useCallback((key: string) => {
    const token = `{{${key}}}`;
    const el = contentRef.current;
    if (!el) {
      const current = (form.getFieldValue('content') as string) ?? '';
      form.setFieldsValue({ content: current + token });
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const current = el.value;
    const next = current.slice(0, start) + token + current.slice(end);
    form.setFieldsValue({ content: next });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }, [form]);

  const handleSubmit = async (values: CreateNotifyTemplateDto & UpdateNotifyTemplateDto) => {
    setSaving(true);
    try {
      if (isEdit && id) {
        await notificationTemplateService.updateNotificationTemplate(id, values);
        message.success('Cập nhật template thành công');
      } else {
        await notificationTemplateService.createNotificationTemplate(values);
        message.success('Tạo template thành công');
      }
      navigate('/notification-templates');
    } catch (error: unknown) {
      message.error((error as { message?: string })?.message || (isEdit ? 'Cập nhật thất bại' : 'Tạo thất bại'));
    } finally {
      setSaving(false);
    }
  };

  const dragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/notification-templates')}>
            Quay lại
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? 'Sửa Notification Template' : 'Tạo Notification Template'}
          </Title>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={16}>
              <Card title="Nội dung" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="title"
                      label="Tiêu đề"
                      rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                    >
                      <div
                        onDragOver={dragOver}
                        onDrop={(e) => handleDropToInput(e, 'title', titleCursorRef)}
                      >
                        <Form.Item name="title" noStyle>
                          <Input
                            placeholder="Nhập tiêu đề template"
                            style={draggedParam ? { background: '#f0f5ff' } : undefined}
                            onFocus={(e) => { titleCursorRef.current = e.target.selectionStart ?? 0; }}
                            onBlur={(e) => { titleCursorRef.current = e.target.selectionStart ?? 0; }}
                            onClick={(e) => { titleCursorRef.current = (e.target as HTMLInputElement).selectionStart ?? 0; }}
                            onKeyUp={(e) => { titleCursorRef.current = (e.currentTarget as HTMLInputElement).selectionStart ?? 0; }}
                          />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="notify_title" label="Tiêu đề thông báo">
                      <div
                        onDragOver={dragOver}
                        onDrop={(e) => handleDropToInput(e, 'notify_title', notifyTitleCursorRef)}
                      >
                        <Form.Item name="notify_title" noStyle>
                          <Input
                            placeholder="Nhập tiêu đề push notification"
                            style={draggedParam ? { background: '#f0f5ff' } : undefined}
                            onFocus={(e) => { notifyTitleCursorRef.current = e.target.selectionStart ?? 0; }}
                            onBlur={(e) => { notifyTitleCursorRef.current = e.target.selectionStart ?? 0; }}
                            onClick={(e) => { notifyTitleCursorRef.current = (e.target as HTMLInputElement).selectionStart ?? 0; }}
                            onKeyUp={(e) => { notifyTitleCursorRef.current = (e.currentTarget as HTMLInputElement).selectionStart ?? 0; }}
                          />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12} align="top">
                  <Col flex="auto">
                    <Form.Item
                      name="content"
                      label="Nội dung"
                      rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input.TextArea
                        rows={10}
                        placeholder="Nhập nội dung. Kéo thả hoặc click param để chèn."
                        style={draggedParam ? { background: '#f0f5ff' } : undefined}
                        ref={(node) => {
                          contentRef.current = node?.resizableTextArea?.textArea ?? null;
                        }}
                        onDragOver={dragOver}
                        onDrop={handleDropToContent}
                      />
                    </Form.Item>
                  </Col>
                  <Col style={{ width: 220 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Tham số có sẵn</div>
                    <p style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                      Kéo thả vào tiêu đề hoặc nội dung
                    </p>
                    <div
                      style={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4,
                        padding: 8,
                        background: draggedParam ? '#f0f5ff' : '#fafafa',
                      }}
                    >
                      {Object.entries(TEMPLATE_PARAMS).map(([key, label]) => (
                        <div
                          key={key}
                          draggable
                          onDragStart={(e) => handleDragStart(e, key)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleInsertParam(key)}
                          style={{
                            cursor: 'grab',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            padding: '6px 10px',
                            marginBottom: 6,
                            background: '#fff',
                            userSelect: 'none',
                          }}
                        >

                          <div style={{ fontSize: 13 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={8}>
              <Card title="Cài đặt" style={{ marginBottom: 16 }}>
                <Form.Item name="tenant_id" label="Tenant">
                  <Select
                    placeholder="Chọn tenant (để trống = platform)"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={tenantsLoading}
                    options={tenants.map((t) => ({ value: t.id, label: t.name }))}
                  />
                </Form.Item>

                <Form.Item name="code" label="Code">
                  <Input placeholder="Mã định danh (vd: ORDER_CONFIRM)" />
                </Form.Item>

                <Form.Item name="type" label="Loại">
                  <Select placeholder="Chọn loại" options={TYPE_OPTIONS} />
                </Form.Item>

                <Form.Item name="state" label="Trạng thái">
                  <Select placeholder="Chọn trạng thái" options={STATE_OPTIONS} />
                </Form.Item>
              </Card>

              <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={saving}
                    block
                    size="large"
                  >
                    {isEdit ? 'Lưu thay đổi' : 'Tạo template'}
                  </Button>
                  <Button block onClick={() => navigate('/notification-templates')}>
                    Hủy
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default NotificationTemplateForm;
